<template>
	<div class="setting_ps px-7 py-4">
		<div>
			<v-row no-gutters>
				<v-col cols="12">
					<Row class="flex-align-center flex-justify-space-between">
						<h3 class="reg mt-2 mb-1">
							{{ isOwner ? 'Manage workspace members' : 'Workspace members' }}
						</h3>
						<Row class="flex-align-center">
							<template v-if="canManageAccount">
								<vue-json-to-csv :json-data="csvData" csv-title="Ply members">
									<h5 class="mr-5 link">Export CSV ({{ selected.length ? selected.length : filteredTeamUsers.length }})</h5>
								</vue-json-to-csv>
								<h5 v-if="isOwner" class="link" @click="changeOwner">Change workspace owner</h5>
							</template>
							<PrimaryButton class="ml-5" v-if="canInviteUsers" @click="inviteUsers">Add Member</PrimaryButton>
						</Row>
					</Row>
					<Row v-if="canManageAccount">
						{{ membersCount }} members - <SettingsManageUsersTooltip> see details</SettingsManageUsersTooltip>
					</Row>
					<Row class="flex-align-center flex-justify-space-between mt-4">
						<div class="flex-1">
							<v-select
								class="slct set mt-1"
								style="width: 220px"
								:items="manageItems"
								hide-details
								outlined
								dense
								v-model="showFilter"
								append-icon="fa fa-chevron-down"
								item-text="title"
								item-value="value"
							/>
						</div>
						<div class="flex-1" />
						<div class="flex-1">
							<v-text-field
								class="search-field-input"
								v-model="search"
								append-icon="mdi-magnify"
								label="Search"
								single-line
								hide-details
								outlined
							/>
						</div>
					</Row>
					<v-row no-gutters class="mt-5 align-center">
						<v-col class="text-left">
							<Row v-if="canManageAccount" class="flex-align-center">
								<h5 class="mr-4">{{ filteredTeamUsers.length }} {{ filterDescription }} users, {{ selected.length }} selected</h5>
								<AppDropdownMenu :disabled="selected.length === 0" :items="bulkActions" content-class="men" min-width="150" offset-y>
									<template #activator="{ value }">
										<AnemicButton class="bulk-action-button" :class="{ disabled: selected.length === 0 }" :disabled="selected.length === 0">
											Bulk Actions <v-icon class="chevron" :class="{ rotate: value }">mdi-chevron-down</v-icon>
										</AnemicButton>
									</template>
									<template #list-item="{ item }">
										<Row class="menu-item py-1 flex-align-center">
											{{ item.text }}
											<h3 class="description-text ml-1">({{ amountOfUsersText }})</h3>
										</Row>
									</template>
								</AppDropdownMenu>
							</Row>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" class="mt-3">
							<v-data-table
								:options="{
									itemsPerPage: 50,
								}"
								:footer-props="{
									'items-per-page-options': [10, 20, 30, 40, 50, 100],
								}"
								v-model="selected"
								:headers="headers"
								:items="filteredTeamUsers"
								item-key="id"
								:show-select="canManageAccount"
								class="users_table"
								fixed-header
								:loading="loadingMembers"
								:search="search"
							>
								<template v-slot:item.name="{ item }">
									<InfoAvatar :user="item" showName />
								</template>
								<template v-slot:item.email="{ item }">
									{{ item.email }} <span class="owner">{{ item.user_id == user.id ? '(Me)' : '' }}</span>
								</template>
								<template v-slot:item.role="{ item }">
									{{ getRoleTitle(item.role) }}
									<span class="owner">{{ isAccountOwner(item.user_id) ? '(Owner)' : '' }}</span>
								</template>
								<template v-slot:item.status="{ item }">
									<span style="text-transform: capitalize">
										{{ getStatusTxt(item.status) }}
									</span>
								</template>
								<template v-slot:item.actions="{ item: user }">
									<v-menu right bottom :close-on-content-click="false" v-if="showUserMenu(user)">
										<template v-slot:activator="{ on, attrs }">
											<v-btn icon fab x-small rounded v-bind="attrs" v-on="on">
												<SIcon name="dots-h" class="si" iconColor="#959DA1" style="top: -3px; left: -1px" :width="14"></SIcon>
											</v-btn>
										</template>
										<v-list>
											<v-list-item
												v-for="(item, index) in getMenuItems(user.status)"
												:key="`${getUserKey(user)}_${index}`"
												@click="menuAction(item, user)"
											>
												<v-list-item-title>{{ item.title }}</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
								</template>
							</v-data-table>
						</v-col>
					</v-row>
				</v-col>

				<ManageUsersDialog :action.sync="dialogAction" :showDialog.sync="dialog" :users="dialogUsers" />
			</v-row>
		</div>
	</div>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv';
import { mapActions, mapGetters } from 'vuex';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import { ACTIVE, INACTIVE, INVITED, userStatus, mapRoleToTitle } from '../../components/Settings/user.const';
import ManageUsersDialog from '@/components/Settings/SettingsManageUsersDialog';
import InfoAvatar from '@/components/SettingsManageUsersInfoAvatar';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-dialog.const';
import { hasAccountPermission, teamMembers, isAccountOwner } from '@/store/modules/account/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { ADMIN_ROLE } from '@/utils/account-service.const';
import { pluralize } from '@/utils/language-service';
import SettingsManageUsersTooltip from '@/views/settings/SettingsManageUsersTooltip.vue';
export default {
	name: 'SettingsManageUsers',
	components: {
		ManageUsersDialog,
		InfoAvatar,
		VueJsonToCsv,
		SettingsManageUsersTooltip,
	},
	props: {},
	computed: {
		...mapGetters(['user']),
		...mapGetters('account', ['account', 'loadingMembers']),
		...mapGettersShorthand({ isAccountOwner, hasAccountPermission, teamMembers }),
		membersCount() {
			return this.teamMembers.filter((u) => u.status == 1).length;
		},
		csvData() {
			let users = this.filteredTeamUsers;
			if (this.selected.length > 0) {
				users = this.selected;
			}
			users = users.map((user) => ({
				name: user.name,
				email: user.email,
				role: this.getRoleTitle(user.role),
				status: this.getStatusTxt(user.status),
			}));
			return users;
		},
		dialogUsers() {
			if (this.dialogEditUser) {
				return [this.dialogEditUser];
			} else {
				return this.selected;
			}
		},
		isOwner() {
			return this.isAccountOwner(this.user.id);
		},
		tableHeight() {
			return this.$vuetify.breakpoint.height - 450;
		},
		filteredTeamUsers() {
			let users = this.teamMembers;
			if (this.showFilter) {
				users = this.teamMembers.filter((u) => u.status === this.showFilter);
			}
			users = users.map((user) => ({
				...user,
				isSelectable: this.isSelectable(user),
			}));
			return users;
		},
		isCurrentUserAdminOfWorkspace() {
			return this.user.profile.account.role === ADMIN_ROLE;
		},
		canInviteUsers() {
			return this.hasAccountPermission(AccountPermissions.InviteUsers);
		},
		canManageAccount() {
			return this.hasAccountPermission(AccountPermissions.ChangeAccount);
		},
		filterDescription() {
			switch (this.showFilter) {
				case 1:
					return 'Active';
				case 2:
					return 'Inactive';
				case 3:
					return 'Invited';
				default:
					return '';
			}
		},
		amountOfUsersText() {
			return pluralize('user', this.selected.length, true);
		},
	},
	watch: {
		account: {
			handler() {
				this.getAccountMembers();
			},
			deep: true,
		},
		dialog: {
			handler(newV) {
				if (!newV) {
					this.dialogAction = null;
					this.dialogEditUser = null;
				}
			},
		},
	},
	methods: {
		...mapActions('account', ['getAccountMembers']),
		onActivatorClick(e) {
			this.showMenu = true;
			e.stopPropagation();
		},
		inviteUsers() {
			this.dialogAction = 'send_invitation';
			this.dialog = true;
			this.dialogEditUser = null;
		},
		getRoleTitle(roleCode) {
			return mapRoleToTitle(roleCode);
		},
		getMenuItems(status) {
			var arr = [];
			if (status == 3) {
				arr = [
					{ id: 1, title: 'Revoke invitation', action: 'revoke' },
					{ id: 2, title: 'Send Again', action: 'send_again' },
					{ id: 3, title: 'Edit Role', action: 'edit_role' },
				];
			} else if (status == 1) {
				arr = [
					{ id: 1, title: 'Edit Role', action: 'edit_role' },
					{ id: 2, title: 'Deactivate', action: 'deactivate_user' },
				];
			} else if (status == 2) {
				arr = [{ id: 1, title: 'Reactivate', action: 'reactivate' }];
			}

			return arr;
		},
		getStatusTxt(status) {
			switch (status) {
				case userStatus[INVITED]:
					return INVITED;
				case userStatus[ACTIVE]:
					return ACTIVE;
				case userStatus[INACTIVE]:
					return INACTIVE;
				default:
					return '';
			}
		},
		menuAction(item, user) {
			this.dialog = true;
			this.dialogAction = item.action;
			this.dialogEditUser = user;
		},
		mouseOver(user) {
			if (user.role !== 'OW') {
				this.hoverLine = this.getUserKey(user);
			}
		},
		mouseLeave() {
			this.hoverLine = -1;
		},
		getUserKey(user) {
			return `${user.is_invitation_pending ? 'temp_' : ''}${user.id}`;
		},
		async changeOwner() {
			const res = await AppDialogManagerService.openChangeOwnerDialog({
				members: this.teamMembers,
			});

			if (res === DIALOG_RESPONSES.CHANGE) {
				this.$toast.success('Email was sent to complete this change');
			}
		},
		editUsersRole() {
			this.dialog = true;
			this.dialogAction = 'edit_role';
		},
		deactivateUsers() {
			this.dialog = true;
			this.dialogAction = 'deactivate_user';
		},
		reactivateUsers() {
			this.dialog = true;
			this.dialogAction = 'reactivate';
		},
		isSelectable(user) {
			return (
				(this.isCurrentUserAdminOfWorkspace && !this.isAccountOwner(user.user_id)) ||
				(!this.isCurrentUserAdminOfWorkspace && user.role != ADMIN_ROLE)
			);
		},
		showUserMenu(user) {
			return this.canManageAccount && this.isSelectable(user);
		},
	},
	mounted() {
		this.getAccountMembers();
	},
	data() {
		return {
			bulkActions: [
				{
					text: 'Edit role',
					onClick: () => this.editUsersRole(),
				},
				{
					text: 'Re-Activate',
					onClick: () => this.reactivateUsers(),
				},
				{
					text: 'Deactivate',
					onClick: () => this.deactivateUsers(),
				},
			],
			selected: [],
			search: '',
			headers: [
				{ text: 'Users', value: 'name' },
				{ text: 'Email', value: 'email' },
				{ text: 'Role', value: 'role' },
				{ text: 'Status', value: 'status' },
				{ text: 'Actions', value: 'actions', sortable: false },
			],
			dialogEditUser: null,
			dialogAction: null,
			dialog: false,
			hoverLine: -1,
			manageItems: [
				{ title: 'Show All', value: 0 },
				{ title: 'Active', value: 1 },
				{ title: 'Inactive', value: 2 },
				{ title: 'Invite', value: 3 },
			],
			showFilter: 0,
			showMenu: false,
		};
	},
};
</script>

<style scoped lang="scss">
.bulkActionsMenu {
	display: flex;
	align-items: center;
}
h4.reg {
	font-weight: 400 !important;
}
th {
	border-bottom: 1px solid #93a1bb !important;
	color: #354052 !important;
	font-weight: 600 !important;
	font-size: 12px !important;
	padding-bottom: 0px !important;
	line-height: 20px !important;
	height: 36px !important;
}
td {
	border-bottom: 1px solid #eff1f7 !important;
	.owner {
		font-size: 11px;
		opacity: 0.5;
	}
}
.menu_btn {
	border: 1px solid #959da1 !important;
	width: 25px;
	height: 25px;
	background: #fafcff;
	transition: all 0.2s;
	opacity: 0;
}
.menu_btn.show {
	opacity: 1;
}
.men_card {
	width: 210px;
	z-index: 5;
	border-radius: 2px !important;
	border-bottom-right-radius: 6px !important;
}
.bulk-action-button {
	height: 35px !important;
	font-size: 14px !important;
	color: #6c6adc !important;
	border-radius: 6px !important;
	letter-spacing: 0px !important;
	font-weight: normal !important;
	background: var(--white) !important;
	padding: 0px 2px 0px 10px !important;
	border: 1px solid #6c6adc !important;
	&.disabled {
		border: none !important;
	}
}
.chevron {
	margin-top: 2px;
	margin-left: 6px;
	margin-right: 0px;
	transition: 0.2s;
	font-size: 20px !important;
	&.rotate {
		transition: 0.2s;
		transform: rotate(180deg);
	}
}
.search-field-input {
	&::v-deep {
		&.v-text-field--enclosed .v-input__append-inner {
			margin-top: 6px !important;
		}
		&.v-text-field--outlined .v-label {
			top: 8px !important;
		}
	}
}
.users_table {
	::v-deep {
		.v-input--selection-controls__ripple {
			height: 24px;
			width: 24px;
			left: -13px;
			top: calc(50% - 19px);
		}
		.v-icon.v-icon {
			font-size: 14px !important;
		}
		.v-input--selection-controls__input {
			width: 14px;
			height: 14px;
			border-radius: 2px;
		}
		.theme--light.v-icon {
			color: #6c6adc;
		}
		.v-icon--disabled {
			background: #e3e3e3;
		}
		.theme--light.v-icon.v-icon.v-icon--disabled {
			color: #e3e3e3 !important;
		}
	}
}
.menu-item {
	font-size: 14px;
	color: #262626;
}
.description-text {
	color: #26262680;
}
::v-deep {
	.v-data-table__wrapper {
		overflow: auto !important;
		max-height: 100% !important;
	}
}
</style>
